
import { defineComponent } from 'vue';
import MainLayout from '@/layout/MainLayout.vue';
import { useShowsStore } from '@/store/ShowsStore';
import { GalleryShowsItem, ArtworksRecord } from '@/dataTypes/types';
import { useAuthStore } from '@/store/AuthStore';
// import { useArtworksStore } from "@/store/ArtworksStore";
import { useGalleryStore } from '@/store/GalleriesStore';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { format } from 'date-fns';
import Swal from 'sweetalert2';

export default defineComponent({
  components: { MainLayout, QuillEditor },
  data() {
    let id = +this.$route.params.id || null;
    if (!id) {
      id = null;
    }
    const Auth = useAuthStore();
    const Show = useShowsStore();
    const Gallery = useGalleryStore();
    // const Artworks = useArtworksStore();
    return {
      id,
      Auth,
      Show,
      // Artworks,
      Gallery,
      exhibition: {} as GalleryShowsItem,
      artworks: [] as ArtworksRecord[],
      title: '',
      description: '',
      imageUrl: '',
      uploadedImageFile: null as unknown as File | Blob,
      dateFrom: '',
      dateTo: '',
      curatedBy: '',
      artworksSelected: [] as number[],
      imageFileName: '',
    };
  },
  async created() {
    if (this.Auth.isLoggedIn) {
      if (this.Gallery.galleryArtworks.length > 0) {
        this.artworks = this.Gallery.galleryArtworks;
      } else {
        this.artworks = await this.Gallery.getMyArtworks();
      }
      if (this.id) {
        this.exhibition = await this.Show.fetchGalleryShow(+this.id);
        this.title = this.exhibition.title || '';
        this.description = this.exhibition.description || '';
        this.imageUrl = this.exhibition.image?.original || '';
        this.dateFrom =
          format(new Date(this.exhibition?.date_from), 'yyyy-MM-dd') ||
          format(new Date(), 'yyyy-MM-dd');
        this.dateTo =
          format(new Date(this.exhibition?.date_to), 'yyyy-MM-dd') ||
          format(new Date(), 'yyyy-MM-dd');
        this.curatedBy = this.exhibition.curated_by;
        this.artworksSelected = this.exhibition.artworks.map(
          (artwork) => artwork.id
        );
      }
    }
  },
  methods: {
    onFileChange(e: any) {
      const file = e.target.files[0];
      this.uploadedImageFile = file;
      this.imageUrl = URL.createObjectURL(file);
      this.imageFileName = file.name;
    },
    async deleteExhibition(id: number | null) {
      if (!id) {
        return;
      }
      const response = await this.Show.DeleteShow(id);
      if (response.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Exhibition deleted',
          icon: 'success',
          confirmButtonText: 'Ok',
          confirmButtonColor: '#000',
        });
        this.$router.push('/shows');
      } else {
        Swal.fire({
          title: 'Error!',
          text: `Failed to delete ${response.message}`,
          icon: 'error',
          confirmButtonText: 'Ok',
          confirmButtonColor: '#000',
        });
      }
    },
    async updateExhibition() {
      // Create a FormData object
      const formData = new FormData();

      // Append form fields to the FormData object
      formData.append('title', this.title);
      formData.append('description', this.description);
      formData.append('date_from', this.dateFrom);
      formData.append('date_to', this.dateTo);
      formData.append('curated_by', this.curatedBy);

      // Append the selected image file
      if (this.uploadedImageFile) {
        formData.append('image', this.uploadedImageFile, this.imageFileName);
      }

      // Append artwork IDs as an array
      this.artworksSelected.forEach((artworkId) => {
        formData.append(`artwork_ids[]`, artworkId.toString());
      });

      if (
        this.title === '' ||
        this.description === '' ||
        this.dateFrom === '' ||
        this.dateTo === '' ||
        this.curatedBy === '' ||
        this.artworksSelected.length === 0
      ) {
        Swal.fire({
          title: 'Error!',
          text: 'Please fill all the required fields',
          icon: 'error',
          confirmButtonText: 'Ok',
          confirmButtonColor: '#000',
        });
        return;
      }

      if (this.id) {
        const response = await this.Show.UpdateExhibition(this.id, formData);
        if (response) {
          Swal.fire({
            title: 'Success!',
            text: 'Exhibition updated',
            icon: 'success',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#000',
          });
          this.$router.push('/shows');
        } else {
          Swal.fire({
            title: 'Error!',
            text: `Failed to update ${response.message}`,
            icon: 'error',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#000',
          });
        }
      } else {
        const response = await this.Show.CreateExhibition(formData);
        if (response) {
          Swal.fire({
            title: 'Success!',
            text: 'Exhibition created',
            icon: 'success',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#000',
          });
          this.$router.push('/shows');
        } else {
          Swal.fire({
            title: 'Error!',
            text: `Failed to create ${response.message}`,
            icon: 'error',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#000',
          });
        }
      }
    },
  },
  computed: {
    artworksTitleAndFullName(): { title: string; id: number | null }[] {
      return this.artworks.map((artwork: ArtworksRecord) => {
        return {
          title: `${artwork.title} - ${artwork.artist.full_name}`,
          id: artwork.id,
        };
      });
    },
    getArtworksIds(): number[] {
      const ids = this.artworks.map((artwork: ArtworksRecord) => {
        // compare the title and full name of the artwork with the selected artworks
        const title = `${artwork.title} - ${artwork.artist.full_name}`;
        if (
          this.artworksSelected.find((artwork) => artwork.toString() === title)
        ) {
          return artwork.id;
        } else {
          return null;
        }
      });
      return ids.filter((id: number | null): id is number => id !== null);
    },
  },
});
